import type { ReactNode } from "react";

import { useUser } from "@/api/hooks/user";
import { getFeatureValue, toFeatureSet } from "@/types/featureSet";

/**
 * Props for FeatureGate.
 *
 * @example
 * <FeatureGate path="tools.map.enabled">
 *   <Map />
 * </FeatureGate>
 */
type FeatureGateProps = {
  path: string;
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Conditionally renders children based on a config path in the feature set.
 * Only renders when the value is strictly `true`.
 *
 * @example
 * <FeatureGate path="tools.map.enabled" fallback={null}>
 *   <MapTool />
 * </FeatureGate>
 */
export const FeatureGate = ({ path, fallback = null, children }: FeatureGateProps) => {
  const { features } = useUser();
  const value = getFeatureValue(features ?? null, path);
  if (value === true) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
